import { useState, useEffect, useRef, useCallback } from 'react'
import { getWsToken, createDeviceWebSocket } from '../api/websocket'
import { useAlertToast } from '../store/alertToastStore'

export interface WebSocketData {
  isConnected: boolean
  bpm: number | null
  piezoValue: number
  sensor2Value: number
  waveformData: number[]
  cameraFrame: string | null
  cameraStatus: { isLive: boolean; lastOnlineAt: string | null } | null
}

export function useDeviceWebSocket(token: string | null): WebSocketData {
  const [isConnected, setIsConnected] = useState(false)
  const [bpm, setBpm] = useState<number | null>(null)
  const [piezoValue, setPiezoValue] = useState<number>(0)
  const [sensor2Value, setSensor2Value] = useState<number>(0)
  const [cameraFrame, setCameraFrame] = useState<string | null>(null)
  const [cameraStatus, setCameraStatus] = useState<{
    isLive: boolean
    lastOnlineAt: string | null
  } | null>(null)

  // Keep the last 200 data points for the waveform
  const waveformRef = useRef<number[]>(Array(200).fill(0))
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectAttempts = useRef(0)
  const maxReconnectAttempts = 5
  const isComponentMounted = useRef(true)

  // Force re-render when waveform updates (optional, but we usually draw canvas via ref)
  // We expose the ref data directly to avoid excessive re-renders
  const [waveformData, setWaveformData] = useState<number[]>(Array(200).fill(0))

  const { addAlertToast } = useAlertToast()

  const connect = useCallback(async () => {
    if (!token || !isComponentMounted.current) return

    try {
      const { token: wsToken } = await getWsToken(token)
      if (!isComponentMounted.current) return

      const ws = createDeviceWebSocket(wsToken)
      wsRef.current = ws

      ws.onopen = () => {
        setIsConnected(true)
        reconnectAttempts.current = 0
      }

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)

          if (data.type === 'alert' && data.alert) {
            addAlertToast(data.alert)
          }

          // Camera streaming events
          if (data.type === 'camera.frame' && data.frame) {
            setCameraFrame(data.frame)
            // A frame arriving also implies the camera is live
            setCameraStatus((prev) =>
              prev && prev.isLive
                ? prev
                : { isLive: true, lastOnlineAt: prev?.lastOnlineAt ?? null },
            )
          }
          if (data.type === 'camera.status') {
            setCameraStatus({
              isLive: !!data.isLive,
              lastOnlineAt: data.lastOnlineAt ?? null,
            })
            if (!data.isLive) {
              setCameraFrame(null)
            }
          }

          if (data.bpm !== undefined) setBpm(data.bpm)
          if (data.piezo !== undefined) {
            setPiezoValue(data.piezo)
            // Update waveform array
            const newWaveform = [...waveformRef.current.slice(1), data.piezo]
            waveformRef.current = newWaveform
            // Throttle state updates for waveform to avoid React performance issues
            // The canvas will read directly from the ref
            setWaveformData(newWaveform)
          }
          if (data.sensor2 !== undefined) setSensor2Value(data.sensor2)
        } catch (e) {
          // console.error('Failed to parse WS message', e)
        }
      }

      ws.onclose = () => {
        setIsConnected(false)
        wsRef.current = null

        // Attempt reconnect
        if (
          isComponentMounted.current &&
          reconnectAttempts.current < maxReconnectAttempts
        ) {
          reconnectAttempts.current += 1
          setTimeout(connect, 5000)
        }
      }

      ws.onerror = (error) => {
        // console.error('WebSocket error', error)
        setIsConnected(false)
      }
    } catch (error) {
      // console.error('Failed to establish WebSocket connection', error)
      if (
        isComponentMounted.current &&
        reconnectAttempts.current < maxReconnectAttempts
      ) {
        reconnectAttempts.current += 1
        setTimeout(connect, 5000)
      }
    }
  }, [token])

  useEffect(() => {
    isComponentMounted.current = true
    connect()

    return () => {
      isComponentMounted.current = false
      if (wsRef.current) {
        wsRef.current.close()
      }
    }
  }, [connect])

  return {
    isConnected,
    bpm,
    piezoValue,
    sensor2Value,
    waveformData: waveformRef.current,
    cameraFrame,
    cameraStatus,
  }
}
